import type { SedeConfig } from './types'
import { THEME_BOOT_SCRIPT, THEME_HEAD_TAGS } from '../sites/verde-valsesia-2/theme/inlineScript'
import photoCredits from '../sites/verde-valsesia-2/media/credits.json'
import type { PhotoCredit } from '../legal/types'

/**
 * Verde Valsesia — redesigned site (slug verde-valsesia-2).
 * Same business/domain as ./verde-valsesia.ts (sedeId 2); this config is
 * the ONLY place the owner fills real company data. Every field marked
 * PLACEHOLDER is gated by src/sedi/placeholders.ts and never rendered.
 * The `seo` block is the fallback head for the SPA shell; every route
 * gets its own head from src/sites/verde-valsesia-2/head.ts.
 */
export const verdeValsesia2: SedeConfig = {
  slug: 'verde-valsesia-2',
  domains: ['verdevalsesia.it', 'www.verdevalsesia.it'],
  sedeId: 2,
  brand: {
    name: 'Verde Valsesia',
    tagline: 'Giardini in quota: ad ogni altitudine, la nostra cura',
    primaryRgb: '1 50 32',
    primaryHoverRgb: '46 107 79',
    inkRgb: '246 241 231',
  },
  contact: {
    email: 'info@verdevalsesia.it',
    privacyEmail: 'privacy@verdevalsesia.it',
    phone: '+39 392 979 6658',        // owner, 2026-09-25
    address: 'Borgosesia (VC)',
    addressStreet: '',                 // PLACEHOLDER — "Via … n"
    postalCode: '13011',
    piva: '01710390020',               // this site reads `legal.piva`; this is only its fallback
    pec: '',                           // PLACEHOLDER — this site reads `legal.pec`; this is only its fallback
  },
  // `founder.foundedYear` is NOT used by this site: the JSON-LD foundingDate
  // comes from `legal.foundingYear` (add it to `legal` once confirmed).
  founder: { name: 'TBD', role: 'Titolare', foundedYear: 2025 },   // PLACEHOLDER name
  legal: {
    legalName: 'Verde Valsesia di Alta Quota S.a.s.',
    piva: '01710390020',               // owner, 2026-09-25
    rea: '',                           // PLACEHOLDER
    pec: '',                           // PLACEHOLDER
    sdi: '',                           // PLACEHOLDER
  },
  // The shared legal section (src/legal/). Photo credits come from the
  // fetch script's credits.json; hand-written entries (map data…) go after
  // them here, never into that file (the script rewrites it).
  legalPages: {
    locales: ['it', 'en'],
    legalForm: 'Società in accomandita semplice',
    city: 'Borgosesia (VC)',
    lastUpdated: '2026-10-16', // privacy/cookie/deletion corrected to the 2026-10-16 legal-corpus audit (§7.18)
    // Own WhatsApp channel answered by the AI assistant: every Sede has its own number (no network default).
    whatsappAi: true,
    // home Reviews.tsx fetches the Google rating (reviews/googleRating.ts → places.googleapis.com).
    googlePlaces: true,
    // The management-platform provider (AVMG), the Sede's processor (art. 28 GDPR).
    platformProvider: 'LEONARDO RICCARDO RAVOT',
    homePath: { it: '/', en: '/en/' },
    header: { logoSrc: '/favicon.svg', tagline: 'di Alta Quota' },
    themeStorageKey: 'vv2-theme',        // shared with the site's ThemeToggle (sites/verde-valsesia-2/theme/theme.ts)
    businessId: 'https://verdevalsesia.it/#business',
    ogImage: '/media/verde-valsesia-2/og/{key}-{locale}.jpg',
    credits: {
      entries: [
        ...(photoCredits as readonly PhotoCredit[]),
        // The "Zone servite" relief map (components/home/ZoneMap.tsx; fields from the map session, 2026-09-24).
        {
          role: 'map-relief',
          kind: 'data',
          title: 'Terrain Tiles (EU-DEM, SRTM, GMTED2010)',
          localizedTitle: {
            it: 'Rilievo ombreggiato della carta Zone servite',
            en: 'Shaded relief of the Areas map',
          },
          // Verbatim, as Tilezen's attribution terms require (licenseUrl).
          author:
            'Europe terrain data produced using Copernicus data and information funded by the European Union - EU-DEM layers; SRTM and GMTED2010 terrain data courtesy of the U.S. Geological Survey; Mapzen',
          license: 'Copernicus EU-DEM terms; USGS public domain',
          licenseUrl: 'https://github.com/tilezen/joerd/blob/master/docs/attribution.md',
          sourceUrl: 'https://registry.opendata.aws/terrain-tiles/',
          sourceLabel: 'Terrain Tiles on AWS Open Data',
        },
        {
          role: 'map-osm',
          kind: 'data',
          title: 'OpenStreetMap data',
          localizedTitle: {
            it: 'Fiumi, lago, ghiacciai, strade, vette e percorsi della carta Zone servite',
            en: 'Rivers, lake, glaciers, roads, peaks and routes on the Areas map',
          },
          author: '© OpenStreetMap contributors (routes computed with OSRM)',
          license: 'ODbL 1.0',
          licenseUrl: 'https://opendatacommons.org/licenses/odbl/1-0/',
          sourceUrl: 'https://www.openstreetmap.org/copyright',
          sourceLabel: 'OpenStreetMap',
        },
      ],
      mediaBase: '/media/verde-valsesia-2',
      notes: {
        it: [
          'Caratteri tipografici: Fraunces e Inter nelle pagine, JetBrains Mono nelle anteprime social; tutti rilasciati con licenza SIL Open Font License 1.1.',
          'Icone e illustrazioni: Verde Valsesia.',
          // The home hero's clips (sites/verde-valsesia-2/media/hero-video.ts).
          'Video di sfondo della home (le Alpi sopra un mare di nubi, una ripresa per ogni momento della giornata): adege e Hans su Pixabay, Pixabay Content License; Major & Wang su Pexels, licenza Pexels. Le riprese sono tagliate, ridimensionate e montate ad anello; quella notturna è ricolorata.',
        ],
        en: [
          'Typefaces: Fraunces and Inter on the pages, JetBrains Mono in the social previews; all released under the SIL Open Font License 1.1.',
          'Icons and illustrations: Verde Valsesia.',
          'Home background videos (the Alps above a sea of cloud, one shot per time of day): adege and Hans on Pixabay, Pixabay Content License; Major & Wang on Pexels, Pexels License. The shots are trimmed, resized and looped; the night one is regraded.',
        ],
      },
      changes: {
        it: 'Le foto sono ritagliate e ridimensionate; le anteprime social aggiungono testo sovrapposto. I dati cartografici sono ritagliati e semplificati; il rilievo è ricampionato, ombreggiato e ricolorato.',
        en: 'Photos are cropped and resized; social previews add overlaid text. Map data are clipped and simplified; the relief is resampled, shaded and recoloured.',
      },
    },
  },
  hours: [],                           // PLACEHOLDER — e.g. [{ days: ['Mo','Tu','We','Th','Fr'], opens: '08:00', closes: '18:00' }]
  socials: {},                         // PLACEHOLDER
  team: [],                            // hidden until filled
  seo: {
    title: 'Verde Valsesia — Giardini, irrigazione smart e manutenzione per seconde case',
    description:
      'Verde Valsesia di Alta Quota S.a.s., Borgosesia: rifacimento giardini, impianti di irrigazione controllabili da remoto con sensori, manutenzione per seconde case e condomini, tree climbing e fresatura ceppi in tutta la Valsesia e sul Lago d’Orta.',
    keywords:
      'giardiniere Valsesia, manutenzione giardini seconde case Valsesia, impianto irrigazione Borgosesia, irrigazione smart, tree climbing Valsesia, fresatura ceppi, verde condominiale Borgosesia, giardiniere Alagna Valsesia, giardiniere Varallo, giardiniere Lago d’Orta',
    ogTitle: 'Verde Valsesia — Giardini in quota',
    // Per-locale OG cards live at og/<route>-<locale>.jpg (head.ts::ogImagePath);
    // the SPA shell's fallback is the Italian home card.
    ogImage: 'https://verdevalsesia.it/media/verde-valsesia-2/og/home-it.jpg',
    ogImageAlt: 'Le Alpi sopra un mare di nubi',
    canonical: 'https://verdevalsesia.it/',
    geo: { region: 'IT-VC', placename: 'Borgosesia', lat: 45.7167, lon: 8.2667 },
    themeColor: '#013220',
    appleTitle: 'Verde Valsesia',
    jsonLd: [],   // per-route JSON-LD comes from src/sites/verde-valsesia-2/jsonld.ts
  },
  // Theme tags BEFORE the boot script: on a saved dark choice the script
  // repaints the theme-color meta, which must already be parsed. Both stay
  // ahead of every stylesheet, so the theme class is set before first paint.
  extraHead: [
    ...THEME_HEAD_TAGS,
    THEME_BOOT_SCRIPT,
    '<link rel="preload" href="/fonts/fraunces/fraunces-latin-var.woff2" as="font" type="font/woff2" crossorigin />',
    // The italic too: every page's kickers use it, above the fold on the
    // service and zone heroes, and with `font-display: swap` they would
    // otherwise paint in Georgia italic first.
    '<link rel="preload" href="/fonts/fraunces/fraunces-latin-var-italic.woff2" as="font" type="font/woff2" crossorigin />',
    '<link rel="stylesheet" href="/fonts/fraunces/fraunces.css" />',
  ],
  copy: {},
}
